import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import {
  downloadEventTypeDataToCsv,
  downloadEventTypeDataToXlsx,
  getEventTypeList,
} from "../../app/features/eventType/eventTypeSlice";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { EventTypeResponse } from "../../app/models/responseModels/eventTypeResponse";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import {
  EVENT_TYPE_COLUMN_NAME,
  EVENT_TYPE_LABEL_NAME,
} from "./EventTypeConstants";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import EventTypeDataFilter from "./EventTypeDataFilter";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import EditEventTypeModal from "./modals/EditEventTypeModal";
import { PAGES } from "../../enums/PagesEnum";
import AddEventTypeModal from "./modals/AddEventTypeModal";

const breadcrumb = ["Event Type"];

const EventType = () => {
  const dispatch = useAppDispatch();
  const { isLoading, eventTypeList } = useSelector(
    (state: RootState) => state.eventType
  );

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.EVENT_TYPE
    )
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [filteredData, setFilteredData] = useState<EventTypeResponse[]>([]);

  // ***** Callbacks *****
  const getEventTypeData = useCallback(async () => {
    try {
      const response = await dispatch(getEventTypeList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "eventTypeList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "eventTypeListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getEventTypeData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getEventTypeData]);

  useEffect(() => {
    if (eventTypeList.length > 0) {
      if (filteredData.length === eventTypeList.length) {
        setFilteredData(eventTypeList);
      }
    } else {
      setFilteredData([]);
    }
  }, [eventTypeList, filteredData]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<EventTypeResponse>[]>(
    () => [
      {
        accessorKey: EVENT_TYPE_COLUMN_NAME.ID,
        header: EVENT_TYPE_LABEL_NAME.ID,
        size: 80,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: EVENT_TYPE_COLUMN_NAME.NAME,
        header: EVENT_TYPE_LABEL_NAME.NAME,
        size: 180,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: EVENT_TYPE_COLUMN_NAME.CATEGORY,
        header: EVENT_TYPE_LABEL_NAME.CATEGORY,
        size: 200,
      },
      {
        accessorKey: EVENT_TYPE_COLUMN_NAME.DESCRIPTION,
        header: EVENT_TYPE_LABEL_NAME.DESCRIPTION,
        size: 300,
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getEventTypeData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadEventTypeDataToCsv(filteredData)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "eventTypeDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "eventTypeDownloadCsvException",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadEventTypeDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "EventType.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "eventTypeDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "eventTypeDownloadXlsxException",
      });
    }
  };

  // ***** Modal Popups *****
  const [editEventTypeModal, openEditEventTypeModal] =
    useSubmitModalWithParameter<EventTypeResponse>(
      (eventTypeRowData, closeEditEventTypeModal) => (
        <EditEventTypeModal
          closeEditEventTypeModal={closeEditEventTypeModal}
          onCompleted={getEventTypeData}
          eventTypeRowData={eventTypeRowData}
        />
      )
    );

  const [addEventTypeModal, openAddEventTypeModal] = useSubmitModal(
    (closeAddEventTypeModal) => (
      <AddEventTypeModal
        closeAddEventTypeModal={closeAddEventTypeModal}
        onCompleted={getEventTypeData}
      />
    )
  );

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<EventTypeResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
        {IsCreatePermission && IsAdmin && (
          <Button
            color="success"
            disabled={isLoading}
            onClick={() => openAddEventTypeModal()}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            Add
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <EventTypeDataFilter
          eventTypeData={eventTypeList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No event types"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={false}
        defaultSortColumnName="id"
        openModal={openEditEventTypeModal}
      />
      {editEventTypeModal}
      {addEventTypeModal}
    </Box>
  );
};

export default EventType;
